import React, { useState, useEffect } from "react";
import { MapPin, ExternalLink, Loader2 } from "lucide-react";
import { reverseGeocode } from "../../utils/geocode";

const LocationCell = ({ location }) => {
    const [address, setAddress] = useState(null);
    const [loading, setLoading] = useState(false);

    const lat = location?.latitude;
    const lng = location?.longitude;
    const city = location?.city;

    useEffect(() => {
        const getAddress = async () => {
            // If we don't have coordinates, we can't do anything
            if (!lat || !lng) return;

            // If we already have a city name that looks like a real place, use it
            if (
                city &&
                city !== "-" &&
                city !== "N/A" &&
                city !== "Unknown" &&
                !city.includes(",") && // coordinates often stored in city field as fallback
                city.length > 2
            ) {
                setAddress(city);
                return;
            }

            setLoading(true);
            try {
                const result = await reverseGeocode(lat, lng);
                setAddress(result);
            } catch (error) {
                console.error("Geocoding failed:", error);
                setAddress(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
            } finally {
                setLoading(false);
            }
        };

        getAddress();
    }, [lat, lng, city]);

    if (!lat || !lng) {
        return <span className="text-zinc-400">-</span>;
    }

    const mapUrl = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=16/${lat}/${lng}`;

    return (
        <div className="flex items-center gap-2 group">
            <div className="flex items-center gap-1.5 min-w-0">
                <MapPin className="w-4 h-4 text-blue-500 shrink-0" />
                <span
                    className="text-sm text-zinc-600 truncate max-w-[180px]"
                    title={loading ? "Finding location..." : address || "Loading..."}
                >
                    {loading ? (
                        <span className="flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            <span className="text-xs italic text-zinc-400">Locating...</span>
                        </span>
                    ) : (
                        address || `${lat.toFixed(4)}, ${lng.toFixed(4)}`
                    )}
                </span>
            </div>
            <a
                href={mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-blue-50 text-zinc-400 hover:text-blue-600 shrink-0"
                title="View on OpenStreetMap"
                onClick={(e) => e.stopPropagation()}
            >
                <ExternalLink className="w-3.5 h-3.5" />
            </a>
        </div>
    );
};

export default LocationCell;
